import { useState } from 'react';
import { ATTENDANCE_STATUS_LABEL, type AttendanceStatus } from '@doit/shared';
import { ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';
import type { TimelinePoint } from '../../api/types';
import { formatDate } from '../../lib/format';

const DOT: Record<AttendanceStatus, string> = {
  PRESENT: 'bg-ok',
  ABSENT: 'bg-danger',
};

/**
 * დასწრების ისტორია: ჯერ კომპაქტური ზოლი (ერთი შეხედვით სურათი),
 * შემდეგ — გაშლადი ცხრილი თემებით.
 */
export function AttendanceTimeline({ timeline }: { timeline: TimelinePoint[] }) {
  const [expanded, setExpanded] = useState(false);

  if (timeline.length === 0) {
    return (
      <p className="rounded-lg bg-canvas px-3 py-2.5 text-[12px] text-ink-mute">
        დასწრება ჯერ არ აღრიცხულა.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* --- ზოლი --- */}
      <div className="flex flex-wrap gap-1">
        {timeline.map((p) => (
          <span
            key={p.sessionId}
            title={`#${p.orderNo} · ${formatDate(p.date)} · ${ATTENDANCE_STATUS_LABEL[p.status]}${
              p.topic ? ` · ${p.topic}` : ''
            }`}
            className={clsx('h-5 w-2.5 rounded-sm', DOT[p.status])}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-mute">
        {(Object.keys(DOT) as AttendanceStatus[]).map((s) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className={clsx('size-2 rounded-sm', DOT[s])} />
            {ATTENDANCE_STATUS_LABEL[s]}
          </span>
        ))}
        <span className="ml-auto">
          {timeline.length} ჩატარებული გაკვეთილი
        </span>
      </div>

      {/* --- ცხრილი --- */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-[12px] font-medium text-brand-700 transition hover:underline"
      >
        {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        {expanded ? 'ცხრილის დამალვა' : 'გაკვეთილების ცხრილი'}
      </button>

      {expanded && (
        <div className="max-h-80 overflow-y-auto rounded-lg border border-line">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-canvas text-left text-[10px] uppercase tracking-wide text-ink-mute">
              <tr>
                <th className="px-3 py-2 font-semibold">#</th>
                <th className="px-3 py-2 font-semibold">თარიღი</th>
                <th className="px-3 py-2 font-semibold">თემა</th>
                <th className="px-3 py-2 font-semibold">დასწრება</th>
              </tr>
            </thead>
            <tbody>
              {[...timeline].reverse().map((p) => (
                <tr key={p.sessionId} className="border-t border-line">
                  <td className="tabular px-3 py-1.5 text-ink-mute">{p.orderNo}</td>
                  <td className="px-3 py-1.5 text-ink-soft">{formatDate(p.date)}</td>
                  <td className="px-3 py-1.5 text-ink-soft">{p.topic ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    <span className="flex items-center gap-1.5">
                      <span className={clsx('size-2 rounded-sm', DOT[p.status])} />
                      {ATTENDANCE_STATUS_LABEL[p.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
